import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import PlusIcon from "../../../../../../../../icons/Plus";
import SortIcon from "../../../../../../../../icons/Sort";
import TrashIcon from "../../../../../../../../icons/Trash";
import { PRICING_MAX_BENEFIT, PRICING_MAX_BENEFITS } from "../../../../../../../../model/site";
import { ListCardAction } from "../../../../../../components/ListCard";
import { Container } from "../../../../../Form/pages/Field/components/OptionsList/styles";

/** A benefit as the list edits it: its text and an id the dragging follows (the plan keeps only the texts). */
export type BenefitRow = { id: string; text: string };

type Props = {
  rows: BenefitRow[];
  onChange: (rows: BenefitRow[]) => void;
  newRow: () => BenefitRow;
};

/** The benefits of a plan: sortable rows with inline text and remove, like the options of a form field. */
export const BenefitsList = ({ rows, onChange, newRow }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.pricing.item.form";

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) return;
    const sorted = Array.from(rows);
    const [moved] = sorted.splice(source.index, 1);
    sorted.splice(destination.index, 0, moved);
    onChange(sorted);
  };

  return (
    <Container>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="benefits" direction="vertical">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {rows.map((row, index) => (
                <Draggable key={row.id} index={index} draggableId={row.id}>
                  {provided => (
                    <div className="item" ref={provided.innerRef} {...provided.draggableProps}>
                      <div className="row">
                        <span
                          className="drag-handle"
                          title={t(`${prefix}.dragBenefit`)}
                          {...provided.dragHandleProps}
                          aria-hidden="true">
                          <SortIcon color="currentColor" />
                        </span>
                        <input
                          type="text"
                          maxLength={PRICING_MAX_BENEFIT}
                          value={row.text}
                          aria-label={t(`${prefix}.benefit`, { count: index + 1 })}
                          placeholder={t(`${prefix}.benefit`, { count: index + 1 })}
                          onChange={e =>
                            onChange(
                              rows.map(other => (other.id === row.id ? { ...other, text: e.target.value } : other))
                            )
                          }
                        />
                        <ListCardAction
                          type="button"
                          $danger
                          title={t(`${prefix}.removeBenefit`, { count: index + 1 })}
                          aria-label={t(`${prefix}.removeBenefit`, { count: index + 1 })}
                          onClick={() => onChange(rows.filter(({ id }) => id !== row.id))}>
                          <TrashIcon size={16} color="currentColor" />
                        </ListCardAction>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder as ReactNode}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {rows.length < PRICING_MAX_BENEFITS ? (
        <button type="button" className="add" onClick={() => onChange([...rows, newRow()])}>
          <PlusIcon width={11} height={11} color="currentColor" />
          {t(`${prefix}.addBenefit`)}
        </button>
      ) : (
        <p className="hint">{t(`${prefix}.benefitsLimit`, { count: PRICING_MAX_BENEFITS })}</p>
      )}
    </Container>
  );
};
